/**
 * /app/frontend/src/lib/tabBadge.js
 *
 * Cross-modal tab badge — gives the user a peripheral-vision signal of
 * what's happening on the page when they've tabbed away. Three things
 * happen together:
 *
 *   1. `document.title` is hijacked to show `(elapsed) label`.
 *   2. The favicon is repainted as a small colored dot in the corner of
 *      the page's original /favicon icon, so the OS-level tab strip
 *      shows the state at a glance.
 *   3. Both restore automatically on `clearBadge()` (or when no caller
 *      has an active badge anymore — see ref-counted store below).
 *
 * Multiple callers can stack badges (e.g. Suno generating + Sora 2 video
 * rendering at the same time). The most recent active badge wins for
 * title + favicon — but on clear, the previous badge re-emerges. This
 * matches what users intuitively expect: closing a Suno tab while Sora
 * is still cooking should NOT silently restore the original title.
 */

import logger from "@/lib/logger";

const COLORS = {
  composing: "#a855f7",   // violet — Suno song
  video: "#06b6d4",       // cyan — Sora 2 video
  ready: "#10b981",       // emerald — anything finished, brief flash
  error: "#f43f5e",       // rose — anything failed
  generic: "#f59e0b",     // amber — fallback
};

const stack = [];          // [{id, label, elapsed, color}]
let originalTitle = "";
let originalIconHref = "";
let originalIconEl = null;
let scheduledRender = false;
let lastFaviconKey = "";   // memo so we don't repaint the canvas every tick

function captureOriginals() {
  if (originalTitle) return;
  originalTitle = document.title || "ELPM Studio Pro";
  const icon = document.querySelector('link[rel="icon"], link[rel="shortcut icon"]');
  if (icon) {
    originalIconEl = icon;
    originalIconHref = icon.href;
  }
}

/** Paint a small dot in the bottom-right of the original favicon and swap
 *  the <link rel=icon> href to the resulting data URL. No-op if we have
 *  no original to start from (e.g. SSR). Repaint is memoized by `key` so
 *  we don't burn CPU during the per-second elapsed tick. */
async function paintFavicon(color) {
  if (!originalIconEl) return;
  const key = color || "_clear";
  if (key === lastFaviconKey) return;
  lastFaviconKey = key;

  if (!color) {
    // Restore — set the href back to the original.
    originalIconEl.href = originalIconHref;
    return;
  }

  try {
    const size = 32;
    const canvas = document.createElement("canvas");
    canvas.width = size; canvas.height = size;
    const ctx = canvas.getContext("2d");

    // Try to draw the original favicon under the dot. If it fails (CORS,
    // SVG quirks, etc.), fall back to a solid gray background.
    const baseImg = new Image();
    baseImg.crossOrigin = "anonymous";
    baseImg.src = originalIconHref;
    await new Promise((resolve) => {
      baseImg.onload = () => { try { ctx.drawImage(baseImg, 0, 0, size, size); } catch (e) { /* tainted */ } resolve(); };
      baseImg.onerror = () => { ctx.fillStyle = "#1e293b"; ctx.fillRect(0, 0, size, size); resolve(); };
      // Failsafe — never wait more than 800 ms.
      setTimeout(resolve, 800);
    });

    // Bottom-right dot with a small dark ring so it pops on any background.
    const r = 10;
    ctx.beginPath();
    ctx.arc(size - r - 2, size - r - 2, r, 0, Math.PI * 2);
    ctx.fillStyle = "#020617";
    ctx.fill();
    ctx.beginPath();
    ctx.arc(size - r - 2, size - r - 2, r - 2, 0, Math.PI * 2);
    ctx.fillStyle = color;
    ctx.fill();

    originalIconEl.href = canvas.toDataURL("image/png");
  } catch (e) {
    // Favicon painting is non-essential — never throw.
    logger.debug("[tabBadge] favicon paint failed", e?.message);
  }
}

/** Schedules a render on the next microtask so rapid pushBadge/updateBadge
 *  calls coalesce into one DOM mutation. */
function scheduleRender() {
  if (scheduledRender) return;
  scheduledRender = true;
  queueMicrotask(() => {
    scheduledRender = false;
    notifySubscribers();
    if (stack.length === 0) {
      document.title = originalTitle || document.title;
      paintFavicon(null);
      return;
    }
    captureOriginals();
    // Top of stack = most-recently-pushed badge wins the display.
    const top = stack[stack.length - 1];
    const elapsedStr = top.elapsed != null ? `(${top.elapsed}s) ` : "";
    document.title = `${elapsedStr}${top.label}`;
    paintFavicon(top.color || COLORS.generic);
  });
}

let _nextId = 0;

/** Push a new badge. Returns its id (use it to update / clear later).
 *  Optional `cancel` callback — when provided, the badge becomes
 *  cancellable: each dropdown row gets an X, plus the dropdown's
 *  "Stop all" button calls cancel for every cancellable entry.
 *  The callback itself is responsible for actually aborting the work
 *  (e.g. AbortController.abort() for fetch streams, clearTimeout for
 *  pollers); tabBadge just removes the entry. */
export function pushBadge({ label = "Working", color = COLORS.generic, elapsed = null, cancel = null } = {}) {
  captureOriginals();
  const id = ++_nextId;
  stack.push({ id, label, elapsed, color, cancel });
  scheduleRender();
  return id;
}

/** Update an existing badge in-place (typical use: tick `elapsed` every
 *  second). Silently no-ops if the id isn't currently in the stack. */
export function updateBadge(id, { label, elapsed, color } = {}) {
  const b = stack.find((x) => x.id === id);
  if (!b) return;
  if (label != null) b.label = label;
  if (elapsed != null) b.elapsed = elapsed;
  if (color != null) b.color = color;
  scheduleRender();
}

/** Remove a badge. If `flashReady` is true, briefly replace it with a
 *  green ✓ "<label> ready!" badge for 3.5 s before fully clearing — this
 *  is the "song ready" UX. */
export function clearBadge(id, flashReady = false, finishedLabel = "Ready") {
  const idx = stack.findIndex((x) => x.id === id);
  if (idx < 0) return;
  if (flashReady) {
    // Swap the badge entry in-place to a ready-flash, then auto-clear it.
    stack[idx] = { id, label: `${finishedLabel} ✓`, color: COLORS.ready, elapsed: null };
    scheduleRender();
    setTimeout(() => {
      const i2 = stack.findIndex((x) => x.id === id);
      if (i2 >= 0) {
        stack.splice(i2, 1);
        scheduleRender();
      }
    }, 3500);
    return;
  }
  stack.splice(idx, 1);
  scheduleRender();
}

/** Cancel a specific in-flight badge by id. Invokes its `cancel` callback
 *  (if set) and removes it. Idempotent — safe to call after the entry has
 *  already been cleared. Returns true if a cancel actually fired. */
export function cancelBadge(id) {
  const b = stack.find((x) => x.id === id);
  if (!b) return false;
  if (typeof b.cancel === "function") {
    try { b.cancel(); } catch (e) { console.warn("[tabBadge] cancel threw", e); }
  }
  const i = stack.findIndex((x) => x.id === id);
  if (i >= 0) { stack.splice(i, 1); scheduleRender(); }
  return true;
}

/** Cancel every cancellable badge currently in the stack. Server-side
 *  tasks without a cancel fn are left alone (they'll clear on their own
 *  when their useSora/poll loop notices the result has landed). Returns
 *  the number actually cancelled. */
export function cancelAll() {
  let n = 0;
  // Iterate a copy because cancel() mutates `stack`.
  for (const b of stack.slice()) {
    if (typeof b.cancel === "function") {
      try { b.cancel(); n++; } catch (e) { console.warn("[tabBadge] cancelAll threw", e); }
    }
  }
  // Drop every cancellable entry; non-cancellable ones stay.
  for (let i = stack.length - 1; i >= 0; i--) {
    if (typeof stack[i].cancel === "function") stack.splice(i, 1);
  }
  if (n > 0) scheduleRender();
  return n;
}

/** Subscribe to changes in the active badge stack. Callback receives an
 *  array of `{id, label, elapsed, color}` (top-most last). Returns an
 *  unsubscribe fn. Used by the header "Now cooking…" pill to render an
 *  aggregated view of all in-flight background tasks. */
const subscribers = new Set();
function notifySubscribers() {
  // Send a shallow copy so consumers don't mutate the internal stack.
  // We include a boolean `cancellable` rather than the live callback so
  // the cancel logic stays inside this module — UI calls cancelBadge(id)
  // by id, never invokes the raw fn directly.
  const snapshot = stack.map((b) => ({
    id: b.id, label: b.label, elapsed: b.elapsed, color: b.color,
    cancellable: typeof b.cancel === "function",
  }));
  for (const fn of subscribers) {
    try { fn(snapshot); } catch (e) { console.warn("[tabBadge] subscriber threw", e); }
  }
}
export function subscribeBadges(fn) {
  subscribers.add(fn);
  // Push the current snapshot immediately so new subscribers get the
  // current state without waiting for the next mutation.
  try {
    fn(stack.map((b) => ({
      id: b.id, label: b.label, elapsed: b.elapsed, color: b.color,
      cancellable: typeof b.cancel === "function",
    })));
  } catch (e) { /* noop */ }
  return () => subscribers.delete(fn);
}

export { COLORS as TAB_BADGE_COLORS };
